import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, EmptyState, ProgressBar, Segmented } from "../ui";
import { Assessment, BarChart, Quiz, Slides, Video } from "../icons";
import {
  getUserModuleReport,
  getUserModuleVideoReport,
} from "../../services/UserReportService";
import { getAnswers } from "../../services/AssessmentService";

/**
 * The learner's own analytics.
 *
 * These were four chart-library canvases with a sixteen-colour rainbow palette
 * and a drag-and-resize grid on top. All four answer the same shape of question
 * — "how much per module" — so they're now one bar primitive rendered four
 * times, in DOM rather than canvas: crisp at any zoom, themed by the same
 * tokens as everything else, and readable at a glance because colour no longer
 * carries meaning it doesn't have.
 *
 * The data contracts are untouched: the same endpoints, the same range params,
 * the same fields.
 */

/* ----------------------------------------------------------------- ranges */

const RANGES = [
  { value: "This Month", label: "1M" },
  { value: "This Week", label: "1W" },
  { value: "Today", label: "1D" },
  { value: "Custom", label: "Custom" },
];

/** `series` values arrive as whole minutes, so 0 means "under a minute". */
const formatMinutes = (minutes) => {
  const total = Math.round(Number(minutes) || 0);
  if (total <= 0) return "<1m";
  if (total < 60) return `${total}m`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

/** The API nests each category name in a single-element array. */
const categoryName = (c) => (Array.isArray(c) ? c[0] : c);

/* ------------------------------------------------------------ primitives */

/**
 * A labelled horizontal bar list. Bars are scaled against the largest value in
 * the set, not against 100, so a set of small numbers still reads as a
 * comparison rather than as four near-empty tracks.
 */
const BarList = ({ rows, tone, formatValue, max }) => {
  const ceiling = max ?? Math.max(...rows.map((r) => r.value), 1);
  return (
    <div>
      {rows.map((row, i) => (
        <div key={`${row.label}-${i}`} className="ds-row" style={{ padding: "9px 0" }}>
          <span
            className="ds-truncate"
            style={{ width: 150, flexShrink: 0, fontSize: 12.5, color: "var(--ink-2)" }}
            title={row.label}
          >
            {row.label}
          </span>
          <span className="ds-grow">
            <ProgressBar
              value={(row.value / ceiling) * 100}
              tone={tone}
            />
          </span>
          <span className="ds-bar__value">{formatValue(row.value)}</span>
        </div>
      ))}
    </div>
  );
};

const CardHead = ({ title, range, onRange, startDate, endDate, onStart, onEnd }) => (
  <>
    <div className="ds-card__head">
      <h3 className="ds-h3">{title}</h3>
      <Segmented options={RANGES} value={range} onChange={onRange} />
    </div>
    {range === "Custom" && (
      <div
        className="ds-row-flex ds-gap-2"
        style={{ padding: "var(--s-3) var(--s-5) 0" }}
      >
        <input
          type="date"
          className="ds-date"
          value={startDate}
          onChange={(e) => onStart(e.target.value)}
        />
        <span className="ds-muted" style={{ fontSize: 12 }}>
          to
        </span>
        <input
          type="date"
          className="ds-date"
          value={endDate}
          onChange={(e) => onEnd(e.target.value)}
        />
      </div>
    )}
  </>
);

const Figure = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{label}</div>
    <div
      className="ds-truncate"
      style={{ fontSize: 15, fontWeight: 650, color: "var(--ink)", marginTop: 2 }}
      title={value}
    >
      {value}
    </div>
  </div>
);

/* ------------------------------------------------- time-per-module cards */

/**
 * Minutes spent per module, for either decks or video. One component because
 * the only differences between the two are the endpoint, the title and the hue.
 */
const TimeAnalysisCard = ({ title, icon, tone, fetcher, overallKey }) => {
  const navigate = useNavigate();
  const [range, setRange] = useState("This Month");
  const [startDate, setStartDate] = useState("Start");
  const [endDate, setEndDate] = useState("End");
  const [rows, setRows] = useState([]);
  const [overall, setOverall] = useState(null);
  const [loading, setLoading] = useState(true);

  // "Custom" is meaningless without dates; seed a sensible yesterday→today.
  useEffect(() => {
    if (range === "Custom") {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      setStartDate(yesterday.toISOString().split("T")[0]);
      setEndDate(today.toISOString().split("T")[0]);
    }
  }, [range]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await fetcher(range, startDate, endDate);
        if (cancelled || !result) return;
        const { categories = [], series = [] } = result;
        setRows(
          categories.map((c, i) => ({
            label: categoryName(c),
            value: Number(series[i]) || 0,
          }))
        );
        setOverall(result[overallKey] || null);
      } catch (e) {
        if (e?.message === "Unauthorized" || e?.message === "Token Expired") {
          localStorage.removeItem("token");
          navigate("/");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range, startDate, endDate, fetcher, overallKey, navigate]);

  // Longest bars first — the answer to "where did my time go" should be top-left.
  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.value - a.value).slice(0, 6),
    [rows]
  );

  return (
    <Card>
      <CardHead
        title={title}
        range={range}
        onRange={setRange}
        startDate={startDate}
        endDate={endDate}
        onStart={setStartDate}
        onEnd={setEndDate}
      />
      <div className="ds-card__body">
        <div
          className="ds-row-flex ds-gap-5"
          style={{ marginBottom: "var(--s-4)" }}
        >
          <Figure label="Total time" value={overall?.totalTimeOfAllModules || "—"} />
          <Figure label="Top module" value={overall?.mostUsedModule?.module || "—"} />
        </div>

        {loading ? (
          <p className="ds-sub">Loading…</p>
        ) : sorted.length ? (
          <BarList rows={sorted} tone={tone} formatValue={formatMinutes} />
        ) : (
          <EmptyState
            icon={icon}
            text="No activity recorded for this period yet."
          />
        )}
      </div>
    </Card>
  );
};

/* -------------------------------------------------------- score-per-item */

/**
 * Best score per attempted item. `module === null` distinguishes a standalone
 * warm up from a module assessment — that's the server's own distinction, kept
 * here so both cards read from the single `getAnswer` payload.
 */
const ScoreAnalysisCard = ({ title, kind, emptyText, emptyAction, icon }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const response = await getAnswers();
      const scores = response?.data?.myScore || [];
      const best = new Map();
      scores
        .filter((s) =>
          kind === "warmup"
            ? s.assessment?.module == null
            : s.assessment?.module != null
        )
        .forEach((s) => {
          const name = s.assessment?.name;
          if (!name) return;
          const pct = Number(s.percentage) || 0;
          if (!best.has(name) || best.get(name) < pct) best.set(name, pct);
        });
      setRows(
        [...best.entries()]
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 6)
      );
      setLoading(false);
    })();
  }, [kind]);

  return (
    <Card>
      <div className="ds-card__head">
        <h3 className="ds-h3">{title}</h3>
      </div>
      <div className="ds-card__body">
        {loading ? (
          <p className="ds-sub">Loading…</p>
        ) : rows.length ? (
          <BarList
            rows={rows}
            max={100}
            tone="success"
            formatValue={(v) => `${Math.round(v)}%`}
          />
        ) : (
          <EmptyState icon={icon} text={emptyText} action={emptyAction} />
        )}
      </div>
    </Card>
  );
};

/* ------------------------------------------------------------- the section */

export const AnalyticsSection = ({ onGoToWarmUp }) => (
  <section className="ds-section">
    <div className="ds-section__head">
      <div>
        <h2 className="ds-h2">Your activity</h2>
        <p className="ds-sub">
          Time spent and scores achieved, per module.
        </p>
      </div>
    </div>

    <div className="ds-grid ds-grid--2">
      <TimeAnalysisCard
        title="Module wise PPT analysis"
        icon={Slides}
        tone="success"
        fetcher={getUserModuleReport}
        overallKey="pptOverall"
      />
      <TimeAnalysisCard
        title="Module wise video analysis"
        icon={Video}
        fetcher={getUserModuleVideoReport}
        overallKey="videoOverall"
      />
      <ScoreAnalysisCard
        title="Attended warm up analysis"
        kind="warmup"
        icon={Quiz}
        emptyText="No warm ups attended yet. They're the quickest way to check what's stuck."
        emptyAction={
          onGoToWarmUp && (
            <button type="button" className="ds-btn ds-btn--outline ds-btn--sm" onClick={onGoToWarmUp}>
              Go to warm up
            </button>
          )
        }
      />
      <ScoreAnalysisCard
        title="Attended assessment analysis"
        kind="assessment"
        icon={Assessment}
        emptyText="Attempt an assessment and your scores will appear here."
      />
    </div>
  </section>
);

export default AnalyticsSection;
